import { ILinkData } from '@client/core/PageSwitch/ILinkData';
import { ContainerColorProps, containerColorCss } from '@client/core/utils/colors';
import { GetLinkData } from '@client/core/utils/GetLinkData';
import { Colors } from '@client/style/Variables';
import { TextStyle } from '@client/style/TextStyles';
import styled from 'styled-components';

export type ButtonSize = 'xsmall' | 'small' | 'medium';

// type TestType = IStyledComponentBase<"web", FastOmit<React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>, never>> & string;

export const SharedButton = styled.button
	.attrs<{
		colors?: ContainerColorProps;
		invert?: boolean;
		highContrast?: boolean;
		size?: ButtonSize;
		link?: ILinkData | string;
		downloadLink?: string;
	}>(props => {
		if (props.link) {
			const link = GetLinkData(props.link);

			if (link) {
				const handleClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
					ev.preventDefault();
					ev.stopPropagation();

					if (props.onClick && props.onClick !== handleClick) {
						props.onClick(ev);
					}

					let newTab = ev.button === 1;
					if (!newTab) {
						if (window.navigator.userAgent.includes('win')) {
							newTab = ev.ctrlKey;
						} else {
							newTab = ev.metaKey;
						}
					}

					link.open({
						target: newTab ? '_blank' : undefined,
					});
				};

				return {
					...props,
					as: 'a',
					href: link.href,
					target: link.target,
					'aria-label': link.title,
					onClick: handleClick,
				};
			}
		} else if (props.downloadLink) {
			return {
				...props,
				as: 'a',
				href: props.downloadLink,
				download: true,
				target: '_blank',
			};
		}

		return props;
	})
	.withConfig({
		shouldForwardProp: prop => !['colors', 'size', 'link', 'downloadLink', 'invert', 'highContrast'].includes(prop),
	})`
	${props =>
		containerColorCss(props.colors, {
			invert: props.invert,
			highContrast: props.highContrast,
		})}

	display: flex;
	justify-content: center;
	align-items: center;
	gap: 10px;
	pointer-events: auto;
	width: auto;
	position: relative;
	${TextStyle.family('title')}
	cursor: pointer;

	&:disabled {
		opacity: 0.5;
		pointer-events: none;
	}

	&:focus-visible {
		outline: 1px auto;
	}
`;

export const PillButton = styled(SharedButton)<{ $border?: string }>`
	border-radius: 25px;
	border: 1px solid ${props => props.$border ?? 'transparent'};
	padding: ${props => (props.size === 'small' ? '10px 24px' : '12px 24px')};
	min-height: 42px;
	${TextStyle.size('small', 'xlarge', 'laptop')}

	transition: transform 0.5s;
	transition-timing-function: cubic-bezier(0.2, 3, 0.4, 1);

	&:hover {
		transform: scale(1.1);
	}

	&:active {
		transform: scale(0.95);
	}
`;

export const RoundButton = styled(SharedButton).attrs(props => ({
	colors: props.colors ?? Colors.MainMenuButtonBackground,
}))`
	width: ${props => (props.size === 'small' ? '40px' : props.size === 'xsmall' ? '30px' : '56px')};
	height: ${props => (props.size === 'small' ? '40px' : props.size === 'xsmall' ? '30px' : '56px')};
	border-radius: 90%;

	transition: transform 0.5s;
	transition-timing-function: cubic-bezier(0.2, 3, 0.4, 1);

	&:hover {
		transform: scale(1.1);
	}

	&:active {
		transform: scale(0.95);
	}
`;
